import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/* ── Count-up hook ────────────────────────────────────────── */
function useCountUp(target, duration, active) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    const reduced =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setValue(target);
      return;
    }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - (1 - p) ** 3;
      setValue(parseFloat((eased * target).toFixed(1)));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return value;
}

/* ── Taxonomy layer data ──────────────────────────────────── */
const LAYERS = [
  { count: 9,  label: "Core Emotions",         fill: 0.28 },
  { count: 33, label: "Transitional States",   fill: 0.56 },
  { count: 8,  label: "Involuntary Responses", fill: 0.72 },
  { count: 9,  label: "Dramatic States",       fill: 0.90 },
];

/* ── Shared CTA button ────────────────────────────────────── */
function CTAButton({ onClick, style }) {
  return (
    <button
      onClick={onClick}
      data-testid="landing-cta-btn"
      className="landing-cta-btn"
      style={{
        background: "var(--accent-gold)",
        color: "var(--surface-page)",
        border: "none",
        borderRadius: "9999px",
        padding: "0.875rem 2.5rem",
        fontSize: "1rem",
        fontFamily: "'Lora', serif",
        fontWeight: 700,
        cursor: "pointer",
        letterSpacing: "0.04em",
        transition: "opacity 0.2s, transform 0.2s",
        ...style,
      }}
    >
      Experience the Demo →
    </button>
  );
}

/* ── Main component ───────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const statRef = useRef(null);
  const taxRef  = useRef(null);
  const [statActive, setStatActive] = useState(false);
  const [taxVisible, setTaxVisible] = useState(false);

  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setStatActive(true); },
      { threshold: 0.5 }
    );
    if (statRef.current) obs.observe(statRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setTaxVisible(true); },
      { threshold: 0.15 }
    );
    if (taxRef.current) obs.observe(taxRef.current);
    return () => obs.disconnect();
  }, []);

  const score = useCountUp(9.1, 1800, statActive);

  return (
    <div
      data-testid="landing-page"
      style={{
        background: "var(--surface-page)",
        color: "var(--ink-primary)",
        fontFamily: "'Lora', serif",
        overflowX: "hidden",
      }}
    >
      {/* ══ HERO ══════════════════════════════════════════════════════ */}
      <section
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "6rem 2rem 4rem",
          overflow: "hidden",
          textAlign: "center",
        }}
      >
        {/* Floating orbs */}
        {[
          { top: "8%",  left: "6%",  size: 420, color: "rgba(176,141,87,0.07)",  delay: "0s",   duration: "14s" },
          { top: "60%", left: "72%", size: 360, color: "rgba(200,175,130,0.06)", delay: "4s",   duration: "18s" },
          { top: "30%", left: "82%", size: 280, color: "rgba(176,141,87,0.05)",  delay: "8s",   duration: "11s" },
          { top: "72%", left: "2%",  size: 320, color: "rgba(215,195,155,0.07)", delay: "2s",   duration: "16s" },
        ].map((orb, i) => (
          <div
            key={i}
            aria-hidden="true"
            style={{
              position: "absolute",
              top: orb.top,
              left: orb.left,
              width:  orb.size,
              height: orb.size,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${orb.color} 0%, transparent 70%)`,
              filter: "blur(40px)",
              animation: reduced
                ? "none"
                : `orb-float ${orb.duration} ${orb.delay} ease-in-out infinite`,
              pointerEvents: "none",
            }}
          />
        ))}

        {/* Wordmark */}
        <h1
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: "clamp(3.2rem, 9vw, 7.5rem)",
            fontWeight: 900,
            letterSpacing: "0.14em",
            lineHeight: 1,
            color: "var(--ink-primary)",
            marginBottom: "1.75rem",
            animation: reduced ? "none" : "page-enter 0.9s ease forwards",
            opacity: reduced ? 1 : 0,
            textRendering: "optimizeLegibility",
          }}
        >
          ARC<span style={{ color: "var(--accent-gold)", fontStyle: "normal" }}>—</span>SCRIPT
        </h1>

        {/* Tagline */}
        <p
          style={{
            fontFamily: "'Lora', serif",
            fontStyle: "italic",
            fontSize: "clamp(1.05rem, 2.4vw, 1.35rem)",
            color: "var(--ink-secondary)",
            maxWidth: "480px",
            lineHeight: 1.75,
            marginBottom: "3rem",
            animation: reduced ? "none" : "page-enter 0.9s 0.18s ease forwards",
            opacity: reduced ? 1 : 0,
          }}
        >
          The AI scriptwriting engine with emotional intelligence.
        </p>

        {/* CTA */}
        <div
          style={{
            animation: reduced ? "none" : "page-enter 0.9s 0.36s ease forwards",
            opacity: reduced ? 1 : 0,
          }}
        >
          <CTAButton onClick={() => navigate("/demo")} />
        </div>

        {/* Scroll hint */}
        <div
          style={{
            position: "absolute",
            bottom: "2.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            opacity: 0.35,
            animation: reduced ? "none" : "page-enter 1s 1s ease forwards",
          }}
        >
          <div
            style={{
              width: 1,
              height: 48,
              background: "linear-gradient(to bottom, var(--accent-gold), transparent)",
              margin: "0 auto",
            }}
          />
        </div>
      </section>

      {/* ══ TAXONOMY ══════════════════════════════════════════════════ */}
      <section
        ref={taxRef}
        style={{
          maxWidth: 680,
          margin: "0 auto",
          padding: "5rem 2rem 6rem",
          textAlign: "center",
        }}
      >
        <p
          style={{
            fontFamily: "'Fraunces', serif",
            fontWeight: 600,
            fontSize: "clamp(1.15rem, 2.8vw, 1.5rem)",
            color: "var(--ink-primary)",
            marginBottom: "0.75rem",
            letterSpacing: "0.01em",
          }}
        >
          59 emotional states. 4 layers. One engine.
        </p>

        {/* Layer bars */}
        <div
          style={{ margin: "3rem auto 3rem", maxWidth: 520 }}
        >
          {LAYERS.map((layer, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                marginBottom: "1.1rem",
                opacity: taxVisible || reduced ? 1 : 0,
                transform:
                  taxVisible || reduced
                    ? "translateY(0)"
                    : "translateY(12px)",
                transition: reduced
                  ? "none"
                  : `opacity 0.5s ${i * 0.15}s ease, transform 0.5s ${i * 0.15}s ease`,
              }}
            >
              {/* Count */}
              <span
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: "1.35rem",
                  fontWeight: 700,
                  color: "var(--accent-gold)",
                  width: "2.5rem",
                  textAlign: "right",
                  flexShrink: 0,
                }}
              >
                {layer.count}
              </span>

              {/* Bar + label */}
              <div style={{ flex: 1, textAlign: "left" }}>
                <div
                  style={{
                    height: 2,
                    background: "var(--border-subtle)",
                    borderRadius: 2,
                    overflow: "hidden",
                    marginBottom: "0.35rem",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: taxVisible || reduced ? `${layer.fill * 100}%` : "0%",
                      background: "var(--accent-gold)",
                      transition: reduced
                        ? "none"
                        : `width 0.7s ${i * 0.15 + 0.2}s ease`,
                      borderRadius: 2,
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--ink-secondary)",
                    fontFamily: "'Lora', serif",
                    fontStyle: "italic",
                    letterSpacing: "0.02em",
                  }}
                >
                  {layer.label}
                </span>
              </div>
            </div>
          ))}
        </div>

        <p
          style={{
            fontSize: "0.9rem",
            color: "var(--ink-tertiary)",
            fontStyle: "italic",
            lineHeight: 1.7,
            maxWidth: 440,
            margin: "0 auto",
          }}
        >
          Every scene is scored for emotional authenticity before a single dollar is spent on production.
        </p>
      </section>

      {/* ══ PROOF ══════════════════════════════════════════════════════ */}
      <section
        ref={statRef}
        style={{
          textAlign: "center",
          padding: "5rem 2rem 6rem",
          borderTop: "1px solid var(--border-subtle)",
          borderBottom: "1px solid var(--border-subtle)",
        }}
      >
        <div
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: "clamp(4.5rem, 12vw, 8rem)",
            fontWeight: 900,
            color: "var(--ink-primary)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          {statActive || reduced ? score.toFixed(1) : "0.0"}
          <span
            style={{
              fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
              color: "var(--ink-tertiary)",
              fontWeight: 400,
              marginLeft: "0.3em",
            }}
          >
            / 10
          </span>
        </div>
        <p
          style={{
            marginTop: "1.25rem",
            fontSize: "0.95rem",
            color: "var(--ink-secondary)",
            fontStyle: "italic",
            lineHeight: 1.7,
          }}
        >
          Blind-tested against raw AI{" "}
          <span style={{ color: "var(--ink-tertiary)" }}>(7.6 / 10)</span>
          . Won 6 out of 6 evaluation criteria.
        </p>
      </section>

      {/* ══ FOOTER CTA ════════════════════════════════════════════════ */}
      <section
        style={{
          textAlign: "center",
          padding: "5rem 2rem 4rem",
        }}
      >
        <CTAButton onClick={() => navigate("/demo")} />
        <p
          style={{
            marginTop: "1.25rem",
            fontSize: "0.85rem",
            color: "var(--ink-tertiary)",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            fontFamily: "'Lora', serif",
          }}
        >
          Zero signup. 60 seconds. See it work.
        </p>
      </section>

      {/* ══ FOOTER ════════════════════════════════════════════════════ */}
      <footer
        style={{
          borderTop: "1px solid var(--border-subtle)",
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "0.5rem",
        }}
      >
        <span
          style={{ fontSize: "0.8rem", color: "var(--ink-tertiary)" }}
        >
          © 2026 ArcScript AI
        </span>
        <a
          href="/auth"
          style={{
            fontSize: "0.8rem",
            color: "var(--ink-tertiary)",
            textDecoration: "none",
            borderBottom: "1px solid var(--border-subtle)",
            paddingBottom: "1px",
            transition: "color 0.2s",
          }}
          data-testid="landing-signin-link"
        >
          Sign in
        </a>
      </footer>
    </div>
  );
}
